<!--
	This is used to show a list box in a EndpointTableCell.
	The color of list box can be changed based on the current value by setting displayedItemCSS.
-->

<script setup>
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/vue'
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/20/solid'
import { defineModel } from 'vue'
import { Float } from '@headlessui-float/vue'

const props = defineProps({
	recordId: { type: Number, required: true },//ID of the record in the table this list box is associated with
	label: { type: String, default: '' }, // Label shown above ListBox
	items: { type: Array, default: () => [] },// Array of items (see data format defined above)
	itemClass: { type: String, default: '' },// CSS Classes to style item text
	disabled: { type: Boolean, default: false },//True to disable lis tbox
	width: { type: String, default: 'w-31' },//Use to change width of list box
	showSelectedCheckmark: { type: Boolean, default: true },//True to show checkmark next to selected item
	displayedItemCSS: { type: Object, default: null },//Set color list box background based on selected value Ex: { 'Disabled': '!bg-disabled-subtle !text-label', 'Active': '!bg-primary-lighter !text-label'}
});

const emit = defineEmits(['onItemSelected'])

const selectedItem = defineModel({ default: { id: '', text: '', record_id: 0, disabled: false, itemIcon: false, action: '' } })

function isItemSelected(p_item)
{
	if ((selectedItem.value === null || selectedItem.value.id === null) && (p_item.id === 0 || p_item.id === null))
	{
		return true;
	}

	return p_item.id === selectedItem.value.id
}

/**
 * Called when a user changes the selected value
 * @param p_item Selected item
 */
function onSelected(p_item)
{
	if (selectedItem.value.id !== p_item.id)
	{
		p_item.record_id = props.recordId
		emit('onItemSelected', p_item)
	}
}

/**
 * Adds CSS to the displayed value to change look based on selected value, such as changing for Active/Disabled
 * @param p_item Selected text
 * @returns {string}
 */
function displayedItemStyle(p_item)
{
	try
	{
		if (props.displayedItemCSS === null || !props.displayedItemCSS[p_item]) return '';
		return props.displayedItemCSS[p_item]
	}
	catch (e)
	{
		console.error(e)
	}
	return ""
}

</script>

<template>		<!-- Note that <Float> must contain 2 child elements,
			the first is the reference element, and the second is the floating element.
			It can be a Headless UI component or an HTML element.-->
	<Listbox :class="[props.width]" v-model="selectedItem" :disabled="props.disabled" @update:modelValue="value => onSelected(value)">

		<Float
			as="div"
			class="relative group"
			placement="bottom"
			:offset="4"
			:flip="10"
			floating-as="template"
			portal
			adaptiveWidth
		>
			<ListboxButton
				:class="[
					'grid group/outer w-full cursor-default grid-cols-1 rounded-md py-1.5 pl-3 pr-2 text-left text-label',
					'bg-neutral dark:bg-primary-dark dark:text-label-on-color',
					'!border !border-neutral outline-none',
					disabled ? 'cursor-not-allowed' : 'group-hover:ring-1 group-hover:ring-primary-bold-hover group-hover:border-primary-bold-hover',
					displayedItemStyle(selectedItem.text)]"
			>
				<template #default="{ open }">
					<span class="col-start-1 row-start-1 truncate pr-2">{{ selectedItem.text }}</span>
					<ChevronUpIcon v-if="!props.disabled && open" class="col-start-1 row-start-1 size-5 self-center justify-self-end text-icon-neutral sm:size-4
					dark:text-icon-neutral-on-color group-hover/outer:fill-icon-primary-dark " aria-hidden="true" />
					<ChevronDownIcon v-else-if="!props.disabled" class="col-start-1 row-start-1 size-5 self-center justify-self-end text-icon-neutral sm:size-4
					dark:text-icon-neutral-on-color group-hover/outer:fill-icon-primary-dark" aria-hidden="true" />
				</template>

			</ListboxButton>

			<ListboxOptions class="max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg
			 !border !border-neutral-subtle hover:border-primary-bold-hover
						focus:ring-1 focus:ring-primary-bold-hover focus:border-primary-bold-hover
						group-hover:ring-1 group-hover:ring-primary-bold-hover group-hover:border-primary-bold-hover

				dark:bg-primary-dark dark:shadow-none dark:-outline-offset-1 dark:outline-white/10" :class="[props.width]">
				<ListboxOption as="template" v-for="item in props.items" :key="item.id" :value="item" :disabled="item.disabled"  v-slot="{ active }">
					<li
						:class="[
								'group/option cursor-pointer ',
								active ? 'bg-primary-subtle text-label outline-none dark:bg-primary-darker' : 'bg-neutral dark:bg-primary-dark text-placeholder',
								isItemSelected(item) ? 'bg-primary-subtle text-label outline-none dark:bg-primary-darker' : 'text-placeholder',
								'relative cursor-default select-none py-2 pl-3 pr-3'
								]">
							<span :class="[
								isItemSelected(item) ? 'font-semibold text-label dark:text-label-on-color' : 'font-normal text-placeholder dark:text-primary-light',
								'hover:text-label dark:text-label-on-color block truncate',
								]"
							>
								{{ item.text.length === 0 ? "&nbsp;" : item.text }}
							</span>

						<span v-if="isItemSelected(item) && showSelectedCheckmark" :class="[
								active ? 'text-label  dark:text-label-on-color' : 'text-placeholder dark:text-primary-light',
								'absolute inset-y-0 right-0 flex items-center pr-1']">
								<CheckIcon class="size-4" aria-hidden="true" />
							</span>
					</li>
				</ListboxOption>
			</ListboxOptions>
		</Float>
	</Listbox>
</template>

<style scoped>

</style>